/**
 * Service de gestion des tâches (titre, description, date d’échéance)
 * Données privées : filtrées par user_id (session / impersonation).
 */

import { supabase } from '../supabaseClient';
import { getImpersonatedUserIdFromStorage } from '../../contexts/ImpersonationContext';

export interface Task {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  due_date: string;
  completed: boolean;
  /** Ordre manuel (plus petit = affiché plus haut). Défaut côté BD : 0. */
  user_order: number;
  created_at: string;
  updated_at: string;
}

export interface TaskCreatePayload {
  title: string;
  description?: string | null;
  due_date: string;
}

export interface TaskUpdatePayload {
  title?: string;
  description?: string | null;
  due_date?: string;
  completed?: boolean;
  user_order?: number;
}

export interface GetTasksFilters {
  month?: number; // 0-11
  year?: number;
  /** 'all' | 'this_month' | 'upcoming' - optionnel, appliqué côté client si month/year non fournis */
  scope?: 'all' | 'this_month' | 'upcoming';
}

function getUserIdForRead(): Promise<string | null> {
  return (async () => {
    const impersonated = getImpersonatedUserIdFromStorage();
    if (impersonated) return impersonated;
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id ?? null;
  })();
}

/**
 * Liste les tâches de l’utilisateur, triées par due_date asc.
 * Filtre optionnel par mois/année (période de la due_date).
 */
export const getTasks = async (
  filters: GetTasksFilters = {}
): Promise<{ data: Task[] | null; error: any }> => {
  try {
    const userId = await getUserIdForRead();
    if (!userId) {
      return { data: null, error: { message: 'Non connecté' } };
    }

    let query = supabase
      .from('tasks')
      .select('*')
      .eq('user_id', userId)
      .order('user_order', { ascending: true })
      .order('due_date', { ascending: true });

    if (filters.month !== undefined && filters.year !== undefined) {
      const start = new Date(filters.year, filters.month, 1);
      const end = new Date(filters.year, filters.month + 1, 0);
      const startStr = start.toISOString().split('T')[0];
      const endStr = end.toISOString().split('T')[0];
      query = query.gte('due_date', startStr).lte('due_date', endStr);
    } else if (filters.year !== undefined) {
      const startStr = `${filters.year}-01-01`;
      const endStr = `${filters.year}-12-31`;
      query = query.gte('due_date', startStr).lte('due_date', endStr);
    }

    const { data, error } = await query;

    if (error) {
      console.error('getTasks error:', error);
      return { data: null, error };
    }

    let list: Task[] = (data ?? []).map((row: Task) => ({
      ...row,
      user_order: row.user_order ?? 0,
    }));

    if (filters.scope && filters.month === undefined && filters.year === undefined) {
      const today = new Date().toISOString().split('T')[0];
      const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
      const endOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0];
      if (filters.scope === 'this_month') {
        list = list.filter(t => t.due_date >= startOfMonth && t.due_date <= endOfMonth);
      } else if (filters.scope === 'upcoming') {
        list = list.filter(t => t.due_date >= today);
      }
    }

    return { data: list, error: null };
  } catch (err) {
    console.error('getTasks exception:', err);
    return { data: null, error: err };
  }
};

/**
 * Crée une tâche pour l’utilisateur connecté.
 */
export const createTask = async (
  payload: TaskCreatePayload
): Promise<{ data: Task | null; error: any }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const impersonated = getImpersonatedUserIdFromStorage();
    if (!user && !impersonated) {
      return { data: null, error: { message: 'Non connecté' } };
    }
    const userId = impersonated || user!.id;

    const { data: maxRows } = await supabase
      .from('tasks')
      .select('user_order')
      .eq('user_id', userId)
      .order('user_order', { ascending: false })
      .limit(1)
      .maybeSingle();

    const nextOrder = (maxRows?.user_order != null ? maxRows.user_order : -1) + 1;

    const { data, error } = await supabase
      .from('tasks')
      .insert({
        user_id: userId,
        title: payload.title.trim(),
        description: payload.description?.trim() || null,
        due_date: payload.due_date,
        user_order: nextOrder,
      })
      .select()
      .single();

    if (error) {
      console.error('createTask error:', error);
      return { data: null, error };
    }
    return { data: data as Task, error: null };
  } catch (err) {
    console.error('createTask exception:', err);
    return { data: null, error: err };
  }
};

/**
 * Met à jour une tâche (champs fournis uniquement).
 */
export const updateTask = async (
  id: string,
  payload: TaskUpdatePayload
): Promise<{ data: Task | null; error: any }> => {
  try {
    const updates: Record<string, unknown> = {
      ...(payload.title !== undefined && { title: payload.title.trim() }),
      ...(payload.description !== undefined && { description: payload.description?.trim() || null }),
      ...(payload.due_date !== undefined && { due_date: payload.due_date }),
      ...(payload.completed !== undefined && { completed: payload.completed }),
      ...(payload.user_order !== undefined && { user_order: payload.user_order }),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('tasks')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('updateTask error:', error);
      return { data: null, error };
    }
    return { data: data as Task, error: null };
  } catch (err) {
    console.error('updateTask exception:', err);
    return { data: null, error: err };
  }
};

/**
 * Supprime une tâche.
 */
export const deleteTask = async (id: string): Promise<{ error: any }> => {
  try {
    const { error } = await supabase.from('tasks').delete().eq('id', id);
    if (error) {
      console.error('deleteTask error:', error);
      return { error };
    }
    return { error: null };
  } catch (err) {
    console.error('deleteTask exception:', err);
    return { error: err };
  }
};

/**
 * Échange l'ordre de deux tâches (même utilisateur, RLS).
 */
export const swapTasksOrder = async (
  taskIdA: string,
  taskIdB: string
): Promise<{ error: any }> => {
  try {
    const { data: rowA, error: e1 } = await supabase
      .from('tasks')
      .select('id, user_order')
      .eq('id', taskIdA)
      .single();
    const { data: rowB, error: e2 } = await supabase
      .from('tasks')
      .select('id, user_order')
      .eq('id', taskIdB)
      .single();
    if (e1 || e2 || !rowA || !rowB) {
      return { error: e1 || e2 || { message: 'Tâche introuvable' } };
    }
    const { error: u1 } = await supabase
      .from('tasks')
      .update({ user_order: rowB.user_order, updated_at: new Date().toISOString() })
      .eq('id', rowA.id);
    if (u1) return { error: u1 };
    const { error: u2 } = await supabase
      .from('tasks')
      .update({ user_order: rowA.user_order, updated_at: new Date().toISOString() })
      .eq('id', rowB.id);
    return { error: u2 ?? null };
  } catch (err) {
    console.error('swapTasksOrder exception:', err);
    return { error: err };
  }
};
